import { useState } from "react";
import { gql, useQuery, useLazyQuery } from "@apollo/client";
import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material";

import { useAppDispatch } from "../../app/hooks";
import { handleModal } from "../../features/modal/modalSlice";
import { ITextSchedule } from "../../types/mailSchedule";
import { convertAllDates } from "../../utils/function";

const STATS: { key: string; label: string; color?: string }[] = [
  { key: "total", label: "Total" },
  { key: "sent", label: "Sent" },
  { key: "delivered", label: "Delivered", color: "success.main" },
  { key: "queued", label: "Queued" },
  { key: "scheduled", label: "Scheduled" },
  { key: "undelivered", label: "Undelivered", color: "error.main" },
  { key: "failed", label: "Failed", color: "error.main" },
  { key: "unaddressable", label: "Unaddressable", color: "error.main" },
  { key: "canceled", label: "Canceled" },
];

interface IOperationMessage {
  phone: string;
  status: string;
  updatedAt: Date | null;
}

const ROW_HEIGHT = 36;
const LIST_HEIGHT = 300;
const OVERSCAN = 10;

// Only renders the rows in (and just around) the visible window, so a 10,000 number list stays fast
const PhoneList = ({ messages }: { messages: IOperationMessage[] }) => {
  const [scrollTop, setScrollTop] = useState<number>(0);

  const start = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const end = Math.min(messages.length, Math.ceil((scrollTop + LIST_HEIGHT) / ROW_HEIGHT) + OVERSCAN);

  return (
    <Box
      onScroll={(e: React.UIEvent<HTMLDivElement>) => setScrollTop(e.currentTarget.scrollTop)}
      sx={{ mt: 1, height: Math.min(LIST_HEIGHT, messages.length * ROW_HEIGHT), overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}
    >
      <Box sx={{ position: "relative", height: messages.length * ROW_HEIGHT }}>
        {messages.slice(start, end).map((message, index) => (
          <Box
            key={start + index}
            sx={{
              position: "absolute",
              top: (start + index) * ROW_HEIGHT,
              left: 0,
              right: 0,
              height: ROW_HEIGHT,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              boxSizing: "border-box",
            }}
          >
            <span>{message.phone}</span>
            <Box component="span" sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
              {message.status}
              {message.updatedAt ? ` - ${convertAllDates(message.updatedAt, "PPpp")}` : ""}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const csvCell = (value: string) => (/[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

const downloadCsv = (messages: IOperationMessage[], fileName: string) => {
  const rows = [
    ["Phone", "Status", "Updated At"],
    ...messages.map((message) => [message.phone, message.status, message.updatedAt ? convertAllDates(message.updatedAt, "PPpp") : ""]),
  ];
  const csv = rows.map((row) => row.map((cell) => csvCell(cell ?? "")).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const TextOperationStats = ({ textSchedule }: { textSchedule: ITextSchedule }) => {
  const storeDispatch = useAppDispatch();

  const { data, loading, error } = useQuery(GETTEXTOPERATIONSTATS, {
    variables: { textSchedule: textSchedule._id },
    fetchPolicy: "network-only",
  });

  const [selectedStat, setSelectedStat] = useState<{ key: string; label: string } | null>(null);
  const [getMessages, { data: messageData, loading: messagesLoading, error: messagesError }] = useLazyQuery(GETTEXTOPERATIONMESSAGES, {
    fetchPolicy: "network-only",
  });

  const stats = data?.getTextOperationStats;
  const messages: IOperationMessage[] = messageData?.getTextOperationMessages ?? [];

  const handleStatClick = (stat: { key: string; label: string }) => {
    if (!stats?.[stat.key]) return;
    setSelectedStat(stat);
    getMessages({ variables: { textSchedule: textSchedule._id, status: stat.key } });
  };

  return (
    <div>
      <Typography variant="h2" component="div" gutterBottom>
        <strong>{textSchedule.name}</strong>
      </Typography>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error || !stats ? (
        <Box sx={{ my: 2, color: "error.main" }}>{error ? error.message : "Unable to retrieve campaign stats"}</Box>
      ) : (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Box>
              <strong>Status:</strong> {stats.status}
            </Box>
            <Box>
              <strong>Started:</strong> {convertAllDates(stats.createdAt, "PPpp")}
            </Box>
            <Box>
              <strong>Last Updated:</strong> {convertAllDates(stats.updatedAt, "PPpp")}
            </Box>
          </Box>
          <Grid container spacing={2}>
            {STATS.map((stat) => (
              <Grid item xs={6} sm={4} key={stat.key}>
                <Box
                  onClick={() => handleStatClick(stat)}
                  sx={{
                    border: "1px solid",
                    borderColor: selectedStat?.key === stat.key ? "primary.main" : "divider",
                    borderRadius: 1,
                    p: 1.5,
                    textAlign: "center",
                    cursor: stats[stat.key] ? "pointer" : "default",
                    "&:hover": stats[stat.key] ? { borderColor: "primary.main" } : {},
                  }}
                >
                  <Box sx={{ fontSize: "1.5rem", fontWeight: 700, color: stat.color }}>{stats[stat.key] ?? 0}</Box>
                  <Box sx={{ fontSize: "0.85rem", color: "text.secondary" }}>{stat.label}</Box>
                </Box>
              </Grid>
            ))}
          </Grid>
          {selectedStat ? (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <strong>
                  {selectedStat.label} Phone Numbers{!messagesLoading && !messagesError ? ` (${messages.length})` : ""}
                </strong>
                {!messagesLoading && !messagesError && messages.length ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => downloadCsv(messages, `${textSchedule.name} - ${selectedStat.label}.csv`)}
                  >
                    Download CSV
                  </Button>
                ) : null}
              </Box>
              {messagesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : messagesError ? (
                <Box sx={{ my: 1, color: "error.main" }}>{messagesError.message}</Box>
              ) : !messages.length ? (
                <Box sx={{ my: 1, color: "text.secondary" }}>No phone numbers found</Box>
              ) : (
                <PhoneList messages={messages} />
              )}
            </Box>
          ) : null}
        </Box>
      )}
      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button onClick={() => storeDispatch(handleModal(false))} color="info" variant="contained">
          Close
        </Button>
      </Box>
    </div>
  );
};

const GETTEXTOPERATIONSTATS = gql`
  query getTextOperationStats($textSchedule: MongoID!) {
    getTextOperationStats(textSchedule: $textSchedule) {
      campaignId
      status
      operations
      total
      sent
      delivered
      queued
      scheduled
      undelivered
      failed
      unaddressable
      canceled
      createdAt
      updatedAt
    }
  }
`;

const GETTEXTOPERATIONMESSAGES = gql`
  query getTextOperationMessages($textSchedule: MongoID!, $status: String) {
    getTextOperationMessages(textSchedule: $textSchedule, status: $status) {
      phone
      status
      updatedAt
    }
  }
`;

export default TextOperationStats;
